/**
 * FileList (#123): one folder's contents — breadcrumbs, folders first, then
 * documents, states and pager. Renders a FolderListing and the per-item
 * rights it carries; knows no services, dialogs or routes (the handlers do).
 * The Files module uses it for the organization's documents; the Nextcloud
 * folder (part 3 of #98) reuses it with relabelled columns, and shows each
 * file's analysis state with Load into IMSight / Open (part 4).
 */

import template from './file-list.template.html?raw';
import type { TranslationParams } from '../i18n/ui-manager.types';
import {
  NEXTCLOUD_MOUNT_ID,
  type FileItem,
  type FolderItem,
  type FolderListing,
} from '../core/files-service';
import { formatFileSize } from '../core/utilities/formatting';

export interface FileListHandlers {
  folderHref(id: string | null): string;
  onOpen(item: FileItem): void;
  onMove(item: FileItem): void;
  onDeleteFile(item: FileItem, button: HTMLButtonElement): void;
  onRenameFolder(item: FolderItem): void;
  onDeleteFolder(item: FolderItem, button: HTMLButtonElement): void;
  onRetry(): void;
  onPage(page: number): void;
  /** #98 part 4: load a Nextcloud file into IMSight (analyze it), also Load new version. */
  onAnalyze(item: FileItem, button: HTMLButtonElement): void;
  /** #98 part 4: open a Nextcloud file's stored analysis. */
  onOpenStored(item: FileItem): void;
  /** #140: with the flag on, a Nextcloud file offers Add to collection instead of the analysis. */
  onAddToCollection?(item: FileItem, button: HTMLButtonElement): void;
  /** #140: with the flag on, a failed own collection document is uploaded again in its place. */
  onRetryUpload?(item: FileItem): void;
}

export interface FileListOptions {
  t: (key: string, params?: TranslationParams) => string;
  handlers: FileListHandlers;
  rootLabelKey: string;
  emptyRootKey: string;
  emptyFolderKey: string;
  columns?: { by: string; date: string };
}

export interface FileListView {
  readonly breadcrumbs: HTMLElement;
  readonly body: HTMLElement;
  render(listing: FolderListing): void;
  showLoading(): void;
  showError(text: string): void;
  clear(): void;
  /** #98 part 3: Owner/Modified in Nextcloud; no argument = the defaults. */
  setColumns(columns?: { by: string; date: string }): void;
  /** #98 part 4: other empty texts (Nextcloud: no upload hint); no argument = the defaults. */
  setEmptyKeys(keys?: { root: string; folder: string }): void;
}

const FOLDER_ICON =
  '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 3A1.5 1.5 0 0 1 3 1.5h3.09a1.5 1.5 0 0 1 1.06.44l.91.91a.5.5 0 0 0 .35.15H13A1.5 1.5 0 0 1 14.5 4.5v8A1.5 1.5 0 0 1 13 14H3A1.5 1.5 0 0 1 1.5 12.5z"/></svg>';

const CLOUD_ICON =
  '<svg data-mount-icon aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 18a4 4 0 0 1-.6-7.96A5.5 5.5 0 0 1 17 8.5a4.75 4.75 0 0 1 .5 9.5z"/></svg>';

const DEFAULT_COLUMNS = {
  by: 'modules.files.colUploadedBy',
  date: 'modules.files.colDate',
};

/** #140: collection status label; an unknown status reads as still preparing. */
function collectionStatusKey(status: string): string {
  const known = ['queued', 'preparing', 'ready', 'failed'];
  return `modules.collection.status.${known.includes(status) ? status : 'preparing'}`;
}

/** Created date, or a dash when the proxy has none (Nextcloud mount rows). */
function dateText(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

export function createFileList(options: FileListOptions): FileListView {
  const { t, handlers } = options;

  const breadcrumbs = document.createElement('nav');
  breadcrumbs.className = 'files-module__crumbs';
  breadcrumbs.dataset.filesBreadcrumbs = '';
  const body = document.createElement('div');
  body.className = 'file-list';
  body.innerHTML = template;

  const $ = <T extends HTMLElement>(sel: string) => body.querySelector<T>(sel)!;
  const setColumns = (
    columns: { by: string; date: string } = options.columns ?? DEFAULT_COLUMNS
  ): void => {
    $('[data-files-col-by]').dataset.i18n = columns.by;
    $('[data-files-col-by]').textContent = t(columns.by);
    $('[data-files-col-date]').dataset.i18n = columns.date;
    $('[data-files-col-date]').textContent = t(columns.date);
  };
  setColumns();
  const defaultEmptyKeys = {
    root: options.emptyRootKey,
    folder: options.emptyFolderKey,
  };
  let emptyKeys = defaultEmptyKeys;
  const setEmptyKeys = (
    keys: { root: string; folder: string } = defaultEmptyKeys
  ): void => {
    emptyKeys = keys;
  };

  let current: FolderListing | null = null;

  $('[data-files-retry]').addEventListener('click', () => handlers.onRetry());
  $('[data-files-prev]').addEventListener('click', () => {
    if (current) handlers.onPage(Math.max(1, current.page - 1));
  });
  $('[data-files-next]').addEventListener('click', () => {
    if (current) handlers.onPage(current.page + 1);
  });

  function showState(state: 'loading' | 'empty' | 'error' | null): void {
    body
      .querySelectorAll<HTMLElement>('[data-files-state]')
      .forEach((el) => (el.hidden = el.dataset.filesState !== state));
  }

  function button(
    label: string,
    name: string,
    data: string,
    id: string,
    onClick: (b: HTMLButtonElement) => void,
    danger = false
  ): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = danger ? 'btn btn-link text-danger' : 'btn btn-link';
    b.dataset[data] = id;
    b.textContent = label;
    b.setAttribute('aria-label', `${label} ${name}`);
    b.addEventListener('click', () => onClick(b));
    return b;
  }

  function cell(text: string): HTMLTableCellElement {
    const td = document.createElement('td');
    td.textContent = text;
    return td;
  }

  function folderRow(item: FolderItem): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.dataset.folderRow = item.id;
    const name = document.createElement('td');
    if (item.available === false) {
      // An exposed Nextcloud folder that was moved, renamed or deleted: no link.
      const label = document.createElement('span');
      label.className =
        'files-module__folder-link files-module__folder-link--off';
      label.insertAdjacentHTML('afterbegin', FOLDER_ICON);
      label.append(item.name);
      const marker = document.createElement('span');
      marker.className = 'files-module__marker';
      marker.textContent = t('modules.files.unavailable');
      name.append(label, marker);
    } else {
      const link = document.createElement('a');
      link.href = handlers.folderHref(item.id);
      link.dataset.folderLink = item.id;
      link.className = 'files-module__folder-link';
      link.insertAdjacentHTML(
        'afterbegin',
        item.id === NEXTCLOUD_MOUNT_ID ? CLOUD_ICON : FOLDER_ICON
      );
      link.append(item.name);
      name.append(link);
    }
    if (item.mountType === 'shared' || item.mountType === 'group') {
      const mount = document.createElement('span');
      mount.className = 'files-module__mount';
      mount.textContent = t(
        item.mountType === 'shared'
          ? 'modules.files.mountShared'
          : 'modules.files.mountGroup'
      );
      name.append(mount);
    }
    const actions = document.createElement('td');
    if (item.canRename) {
      actions.append(
        button(
          t('modules.files.rename'),
          item.name,
          'folderRename',
          item.id,
          () => handlers.onRenameFolder(item)
        )
      );
    }
    if (item.canDelete) {
      actions.append(
        button(
          t('modules.files.delete'),
          item.name,
          'folderDelete',
          item.id,
          (b) => handlers.onDeleteFolder(item, b),
          true
        )
      );
    }
    tr.append(
      name,
      cell('—'),
      cell(item.createdBy?.name ?? '—'),
      cell(dateText(item.createdAt)),
      cell('—'),
      actions
    );
    return tr;
  }

  /** A Nextcloud file with an analysis state (#98 part 4): label, hint, actions by rights. */
  function analysisRow(
    item: FileItem & { analysis: NonNullable<FileItem['analysis']> }
  ): { status: HTMLTableCellElement; actions: HTMLTableCellElement } {
    const { state } = item.analysis;
    const status = document.createElement('td');
    status.dataset.fileStatus = state;
    const label = document.createElement('span');
    label.dataset.fileStatusLabel = '';
    label.textContent = t(`modules.files.analysis.${state}`);
    status.append(label);
    if (state === 'analyzing') {
      const spinner = document.createElement('span');
      spinner.className = 'spinner-border spinner-border-sm me-1';
      spinner.setAttribute('aria-hidden', 'true');
      status.prepend(spinner);
    }
    const hint =
      state === 'outdated'
        ? 'modules.files.analysis.hintOutdated'
        : state === 'changed'
          ? 'modules.files.analysis.hintChanged'
          : item.analysis.error && state !== 'failed'
            ? 'modules.files.analysis.hintFailed'
            : null;
    if (hint) {
      const small = document.createElement('small');
      small.className = 'files-module__hint';
      small.dataset.fileHint = '';
      small.textContent = t(hint);
      status.append(small);
    }
    const actions = document.createElement('td');
    if (item.canOpen) {
      actions.append(
        button(
          t('modules.files.open'),
          item.name,
          'fileOpenStored',
          item.id,
          () => handlers.onOpenStored(item)
        )
      );
    }
    if (item.canAnalyze) {
      actions.append(
        button(
          t('modules.files.analysis.analyze'),
          item.name,
          'fileAnalyze',
          item.id,
          (b) => handlers.onAnalyze(item, b)
        )
      );
    }
    if (item.canLoadNewVersion) {
      actions.append(
        button(
          t('modules.files.analysis.loadNewVersion'),
          item.name,
          'fileLoadNewVersion',
          item.id,
          (b) => handlers.onAnalyze(item, b)
        )
      );
    }
    return { status, actions };
  }

  /** #140: a Nextcloud file in the collection view: its collection status, Add (again) where it applies. */
  function collectionRow(item: FileItem): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.dataset.fileRow = item.id;
    const state = item.collection?.status;
    const status = document.createElement('td');
    status.dataset.fileStatus = state ?? 'notAdded';
    if (state) {
      const label = document.createElement('span');
      label.dataset.fileStatusLabel = '';
      label.textContent = t(collectionStatusKey(state));
      status.append(label);
    } else {
      status.textContent = '—';
    }
    const actions = document.createElement('td');
    // #145: opens the collection document, the version that was added.
    const documentId = item.collection?.documentId;
    if (state === 'ready' && documentId) {
      actions.append(
        button(t('modules.files.open'), item.name, 'fileOpen', item.id, () =>
          handlers.onOpen({ ...item, id: documentId })
        )
      );
    }
    if (!state || state === 'failed' || item.collection?.changed) {
      actions.append(
        button(
          t(
            state === 'failed'
              ? 'modules.files.retry'
              : 'modules.collection.add'
          ),
          item.name,
          'fileAddToCollection',
          item.id,
          (b) => handlers.onAddToCollection?.(item, b)
        )
      );
    }
    tr.append(
      cell(item.name),
      cell(formatFileSize(item.size, t)),
      cell(item.uploadedBy?.name ?? '—'),
      cell(dateText(item.createdAt)),
      status,
      actions
    );
    return tr;
  }

  function fileRow(item: FileItem): HTMLTableRowElement {
    if (handlers.onAddToCollection && item.status === 'external')
      return collectionRow(item);
    const tr = document.createElement('tr');
    tr.dataset.fileRow = item.id;
    if (item.analysis) {
      const { status, actions } = analysisRow({
        ...item,
        analysis: item.analysis,
      });
      tr.append(
        cell(item.name),
        cell(formatFileSize(item.size, t)),
        cell(item.uploadedBy?.name ?? '—'),
        cell(dateText(item.createdAt)),
        status,
        actions
      );
      return tr;
    }
    const status = cell(
      item.status === 'queued' || item.status === 'preparing'
        ? t(collectionStatusKey(item.status))
        : t(
            `modules.files.status${item.status.charAt(0).toUpperCase()}${item.status.slice(1)}`
          )
    );
    status.dataset.fileStatus = item.status;
    const actions = document.createElement('td');
    // A Nextcloud file without a known analysis state has no actions.
    if (item.status !== 'external') {
      const open = button(
        t('modules.files.open'),
        item.name,
        'fileOpen',
        item.id,
        () => handlers.onOpen(item)
      );
      open.disabled = item.status !== 'ready';
      actions.append(open);
    }
    if (handlers.onRetryUpload && item.status === 'failed' && item.canDelete) {
      actions.append(
        button(t('modules.files.retry'), item.name, 'fileRetry', item.id, () =>
          handlers.onRetryUpload?.(item)
        )
      );
    }
    if (item.canMove) {
      actions.append(
        button(t('modules.files.move'), item.name, 'fileMove', item.id, () =>
          handlers.onMove(item)
        )
      );
    }
    if (item.canDelete) {
      actions.append(
        button(
          t('modules.files.delete'),
          item.name,
          'fileDelete',
          item.id,
          (b) => handlers.onDeleteFile(item, b),
          true
        )
      );
    }
    tr.append(
      cell(item.name),
      cell(formatFileSize(item.size, t)),
      cell(item.uploadedBy?.name ?? '—'),
      cell(dateText(item.createdAt)),
      status,
      actions
    );
    return tr;
  }

  function renderCrumbs(listing: FolderListing): void {
    const ol = document.createElement('ol');
    ol.className = 'breadcrumb mb-0';
    const crumbs: { id: string | null; name: string }[] = [
      { id: null, name: t(options.rootLabelKey) },
      ...listing.path,
    ];
    crumbs.forEach((crumb, index) => {
      const li = document.createElement('li');
      li.className = 'breadcrumb-item';
      if (index === crumbs.length - 1) {
        li.setAttribute('aria-current', 'page');
        const h1 = document.createElement('h1');
        h1.className = 'files-module__title';
        h1.textContent = crumb.name;
        h1.title = crumb.name;
        li.append(h1);
      } else {
        const a = document.createElement('a');
        a.href = handlers.folderHref(crumb.id);
        a.textContent = crumb.name;
        a.title = crumb.name;
        li.append(a);
      }
      ol.append(li);
    });
    breadcrumbs.setAttribute('aria-label', t('modules.files.breadcrumbs'));
    breadcrumbs.replaceChildren(ol);
  }

  return {
    breadcrumbs,
    body,
    setColumns,
    setEmptyKeys,
    render(listing) {
      current = listing;
      renderCrumbs(listing);
      $('[data-files-rows]').replaceChildren(
        ...listing.items.map((item) =>
          item.kind === 'folder' ? folderRow(item) : fileRow(item)
        )
      );
      $('[data-files-state="empty"]').textContent = t(
        listing.folder.id === null ? emptyKeys.root : emptyKeys.folder
      );
      showState(listing.items.length === 0 ? 'empty' : null);
      const pages = Math.max(1, Math.ceil(listing.total / listing.pageSize));
      $('[data-files-pager]').hidden = listing.total <= listing.pageSize;
      $('[data-files-page]').textContent = t('modules.files.page', {
        page: String(listing.page),
        pages: String(pages),
      });
      $<HTMLButtonElement>('[data-files-prev]').disabled = listing.page <= 1;
      $<HTMLButtonElement>('[data-files-next]').disabled =
        listing.page * listing.pageSize >= listing.total;
    },
    showLoading() {
      showState('loading');
    },
    showError(text) {
      current = null;
      $('[data-files-rows]').replaceChildren();
      $('[data-files-error-text]').textContent = text;
      $('[data-files-pager]').hidden = true;
      showState('error');
    },
    clear() {
      current = null;
      breadcrumbs.replaceChildren();
      $('[data-files-rows]').replaceChildren();
      showState(null);
    },
  };
}
